import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Semantic variant for each known event-status value. */
export type StatusPillVariant =
  | 'scheduled'
  | 'in-progress'
  | 'completed'
  | 'cancelled'
  | 'not-scheduled'
  | 'closed'
  | 'open'
  | 'full'
  | 'unknown';

interface StatusConfig {
  label: string;
  variant: StatusPillVariant;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  SCHEDULED: { label: 'Scheduled', variant: 'scheduled' },
  IN_PROGRESS: { label: 'In Progress', variant: 'in-progress' },
  COMPLETED: { label: 'Completed', variant: 'completed' },
  CANCELLED: { label: 'Cancelled', variant: 'cancelled' },
  NOT_SCHEDULED: { label: 'Not Scheduled', variant: 'not-scheduled' },
  CLOSED: { label: 'Closed', variant: 'closed' },
  // #1155: partner-lifecycle states. ACTIVE reuses the green "completed" tint
  // (success); ONBOARDING reuses the amber "in-progress" tint (accent) so the
  // colours stay token-driven from a single source of truth.
  ACTIVE: { label: 'Active', variant: 'completed' },
  ONBOARDING: { label: 'Onboarding', variant: 'in-progress' },
  // #1460: visibility flag rendered as a human label, not a raw boolean.
  PRIVATE: { label: 'Private', variant: 'scheduled' },
  PUBLIC: { label: 'Public', variant: 'closed' },
  // #1153: capacity-DERIVED availability badges (not stored Prisma enum values).
  // OPEN (bookedCount < maxCapacity) reuses the green "completed"/success tint;
  // FULL (bookedCount >= maxCapacity) reuses the amber "in-progress"/accent tint.
  // Kept token-driven via their own variant classes in the SCSS.
  OPEN: { label: 'Open', variant: 'open' },
  FULL: { label: 'Full', variant: 'full' },
  // #1154: Booking-status pills for the event-detail attendee roster (Prisma
  // BookingStatusType). Additive — every variant maps to an EXISTING token-driven
  // class (no new colours). CONFIRMED/BOOKED reuse the green "completed"/success
  // tint (counts toward capacity); PENDING the amber "in-progress"/accent tint;
  // NO_SHOW/LATE_CHECKIN/RESCHEDULED the neutral "not-scheduled" tint. CANCELLED
  // and COMPLETED already have entries above.
  CONFIRMED: { label: 'Confirmed', variant: 'completed' },
  BOOKED: { label: 'Booked', variant: 'completed' },
  PENDING: { label: 'Pending', variant: 'in-progress' },
  NO_SHOW: { label: 'No Show', variant: 'not-scheduled' },
  RESCHEDULED: { label: 'Rescheduled', variant: 'not-scheduled' },
  LATE_CHECKIN: { label: 'Late Check-in', variant: 'not-scheduled' },
};

/**
 * StatusPill — semantic status indicator (token-driven, no Bootstrap badge classes).
 *
 * Promoted to `@bodyanalytics/ui-core` (#1142) from the booking-local
 * `manage-events/ui` copy so the assessment wizard (#1156) and other shells can
 * reuse it without crossing a feature→shared layer boundary. NO behavior change:
 * the original location now re-exports this component.
 */
@Component({
  selector: 'bodyanalytics-status-pill',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="status-pill" [class]="'status-pill--' + config().variant">
      <span class="status-pill__dot" aria-hidden="true"></span>
      {{ config().label }}
    </span>
  `,
  styleUrls: ['./status-pill.component.scss'],
})
export class StatusPillComponent {
  readonly status = input.required<string>();

  readonly config = computed<StatusConfig>(() => {
    const raw = (this.status() ?? '').toUpperCase();
    return STATUS_CONFIG[raw] ?? { label: raw || 'Unknown', variant: 'unknown' };
  });
}
